import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import BoardModal, { BMCancelButton, BMPrimaryButton } from '../BoardModal';
import DeleteTagModal from '../DeleteTagModal';
import {
  Tag, TagPool, TagScope, TASK_TAG_POOL, DECISION_TAG_POOL,
  getScopedTags, getProjectTagLinks, renameTag, createTag, findTagByName,
} from '../../services/tagService';

// Project Settings → Tag Management. Fix typos/casing by renaming the tag
// record in place: tasks/decisions reference tags through junction lookups,
// so the rename propagates everywhere without touching any junction or
// parent record (deleting + recreating would silently drop attachments).
//
// Listed per pool: this project's Project-scoped tags, plus Tenant-scoped
// tags currently applied to at least one task/decision in this project.
// Task tags and Decision tags stay clearly separated — separate data,
// separate meaning.
//
// PM-only: the parent SettingsTab renders this section behind me?.isPM,
// the same gate every other Project Settings write action uses.

interface Props { pca: IPublicClientApplication; projectId: string; }

// Collapsed by default — with 15-20+ tags per pool this section is a long
// scroll-past. Collapse state is remembered per project in localStorage (a
// trivial nicety; not required to persist).
const collapseKey = (projectId: string) => `dagrun_pm_tagmgmt_collapsed_${projectId}`;

export default function TagManagementSection({ pca, projectId }: Props) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(collapseKey(projectId)) === 'open'; } catch { return false; }
  });
  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(collapseKey(projectId), next ? 'open' : 'collapsed'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="card" style={{ padding: 20, marginTop: 24 }}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          textAlign: 'left', marginBottom: open ? 12 : 0,
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--ink-4)', width: 12 }}>{open ? '▾' : '▸'}</span>
        <span className="eyebrow" style={{ margin: 0 }}>TAG MANAGEMENT</span>
      </button>
      {open && (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5 }}>
            Rename tags in place to fix typos or casing. Renames propagate to every
            task or decision the tag is attached to. Tenant-scoped tags shown here are
            in use on this project but shared tenant-wide — renaming one affects all projects.
          </div>
          <PoolTagList pca={pca} projectId={projectId} pool={TASK_TAG_POOL} label="Task Tags" />
          <div style={{ height: 18 }} />
          <PoolTagList pca={pca} projectId={projectId} pool={DECISION_TAG_POOL} label="Decision Tags" />
        </>
      )}
    </div>
  );
}

function PoolTagList({ pca, projectId, pool, label }: Props & { pool: TagPool; label: string }) {
  const [tags, setTags] = useState<Tag[] | null>(null);
  // Full project+tenant scope, including tags not shown in the list —
  // creation dedupe must check against everything visible to this project.
  const [allScoped, setAllScoped] = useState<Tag[]>([]);
  const [editing, setEditing] = useState<{ id: string; draft: string } | null>(null);
  const [pendingRename, setPendingRename] = useState<{ tag: Tag; newName: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createDraft, setCreateDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [duplicate, setDuplicate] = useState<Tag | null>(null);
  // Delete is offered on Project-scoped tags only — deleting a Tenant tag
  // is Admin-only authority (Tenant Admin Panel).
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null);

  const reload = useCallback(async () => {
    setTags(null); setError(null); setDuplicate(null);
    try {
      const [scoped, links] = await Promise.all([
        getScopedTags(pca, pool, projectId),
        getProjectTagLinks(pca, pool, projectId),
      ]);
      const usedIds = new Set<string>();
      links.forEach(ls => ls.forEach(l => usedIds.add(l.tag.id)));
      setAllScoped(scoped);
      // This project's own tags + tenant tags actually in use here.
      const visible = scoped.filter(t =>
        t.projectId === projectId || (t.scope === TagScope.Tenant && usedIds.has(t.id))
      );
      setTags(visible.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      setTags([]);
      setError(e instanceof Error ? e.message : 'Failed to load tags');
    }
  }, [pca, pool, projectId]);
  useEffect(() => { reload(); }, [reload]);

  // Direct creation — always Project-scoped to this project; same
  // case-insensitive dedupe rule as everywhere else, against the full
  // project+tenant scope.
  const create = async () => {
    const name = createDraft.trim();
    if (!name || creating || tags === null) return;
    const existing = findTagByName(allScoped, name);
    if (existing) { setDuplicate(existing); return; }
    setCreating(true); setError(null);
    try {
      const fresh = await createTag(pca, pool, projectId, name);
      setAllScoped(prev => [...prev, fresh]);
      setTags(prev => [...(prev ?? []), fresh].sort((a, b) => a.name.localeCompare(b.name)));
      setCreateDraft(''); setDuplicate(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setCreating(false); }
  };

  const doRename = async (tag: Tag, newName: string) => {
    setSaving(true); setError(null);
    try {
      await renameTag(pca, pool, tag.id, newName);
      setTags(prev => (prev ?? [])
        .map(t => t.id === tag.id ? { ...t, name: newName } : t)
        .sort((a, b) => a.name.localeCompare(b.name)));
      setEditing(null);
      setPendingRename(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rename failed');
    } finally { setSaving(false); }
  };

  const commitEdit = (tag: Tag) => {
    if (!editing || editing.id !== tag.id) return;
    const newName = editing.draft.trim();
    if (!newName || newName === tag.name) { setEditing(null); return; }
    if (tag.scope === TagScope.Tenant) {
      // Tenant tags are shared — confirm before a tenant-wide rename.
      setPendingRename({ tag, newName });
      return;
    }
    doRename(tag, newName);
  };

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
        <input
          value={createDraft}
          disabled={creating || tags === null}
          onChange={e => { setCreateDraft(e.target.value); setDuplicate(null); }}
          onKeyDown={e => { if (e.key === 'Enter') create(); }}
          placeholder={`New ${pool.key} tag for this project…`}
          style={{ flex: '0 1 260px', fontSize: 13 }}
        />
        <button
          type="button"
          className="btn-secondary"
          onClick={create}
          disabled={creating || !createDraft.trim() || tags === null}
          style={{ whiteSpace: 'nowrap' }}
        >{creating ? 'Creating…' : 'Create Tag'}</button>
      </div>
      {duplicate && (
        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--ink-2)' }}>
          A tag named <strong>{duplicate.name}</strong> already exists
          ({duplicate.scope === TagScope.Tenant ? 'Tenant-wide' : 'this project'}) — no duplicate was created.
        </div>
      )}
      {error && <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
      {tags === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)', padding: '6px 0' }}>Loading…</div>
      ) : tags.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic', padding: '6px 0' }}>
          No tags in use on this project yet.
        </div>
      ) : (
        tags.map(t => {
          const isEditing = editing?.id === t.id;
          const isTenant = t.scope === TagScope.Tenant;
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '0.5px solid var(--border-light)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <input
                    autoFocus
                    value={editing.draft}
                    disabled={saving}
                    onChange={e => setEditing({ id: t.id, draft: e.target.value })}
                    onBlur={() => !pendingRename && commitEdit(t)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitEdit(t);
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    style={{ fontSize: 13, padding: '3px 6px', width: '100%', maxWidth: 280 }}
                  />
                ) : (
                  <button
                    type="button"
                    title="Click to rename"
                    onClick={() => setEditing({ id: t.id, draft: t.name })}
                    style={{
                      background: 'transparent', border: 'none', padding: 0,
                      fontSize: 13, fontFamily: 'var(--font-ui)', color: 'var(--ink)',
                      cursor: 'text', textAlign: 'left',
                    }}
                  >{t.name}</button>
                )}
              </div>
              <span className={`pill ${isTenant ? '' : 'neutral'}`}>{isTenant ? 'Tenant' : 'Project'}</span>
              <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', minWidth: 52, textAlign: 'right' }}>
                {t.useCount} use{t.useCount === 1 ? '' : 's'}
              </span>
              {!isTenant ? (
                <button
                  type="button"
                  title="Delete tag"
                  aria-label={`Delete tag ${t.name}`}
                  onClick={() => setDeleteTarget(t)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15, padding: '0 2px', lineHeight: 1 }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}
                >×</button>
              ) : (
                <span style={{ width: 19 }} />
              )}
            </div>
          );
        })
      )}

      {deleteTarget && (
        <DeleteTagModal
          pca={pca}
          pool={pool}
          tag={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setTags(prev => (prev ?? []).filter(t => t.id !== deleteTarget.id));
            setAllScoped(prev => prev.filter(t => t.id !== deleteTarget.id));
            setDeleteTarget(null);
          }}
        />
      )}

      {pendingRename && (
        <BoardModal open title="Rename tenant-wide tag" onClose={() => { if (!saving) { setPendingRename(null); setEditing(null); } }}
          footer={<>
            <BMCancelButton onClick={() => { setPendingRename(null); setEditing(null); }} disabled={saving} />
            <BMPrimaryButton onClick={() => doRename(pendingRename.tag, pendingRename.newName)} disabled={saving}>
              {saving ? 'Renaming…' : 'Rename Everywhere'}
            </BMPrimaryButton>
          </>}>
          <div style={{ fontSize: 13, color: '#5A6272', lineHeight: 1.5 }}>
            <strong style={{ color: '#0F1117' }}>{pendingRename.tag.name}</strong> is a tenant-wide tag.
            Renaming it to <strong style={{ color: '#0F1117' }}>{pendingRename.newName}</strong> will
            change it everywhere it is used — across all projects, not just this one.
          </div>
        </BoardModal>
      )}
    </div>
  );
}
